/**
 * Internationalization (i18n) utility
 * Loads and provides translations based on selected language
 */

import { getSelectedLanguageId, SUPPORTED_LANGUAGES } from './language.js'
import enTranslations from '../locales/en.json'
import esTranslations from '../locales/es.json'

type TranslationKey = string
type TranslationValue = string | { [key: string]: TranslationValue }
type Translations = { [key: string]: TranslationValue }

const translationMap: Record<string, Translations> = {
  '2': enTranslations,
  '3': esTranslations,
}

let translations: Translations = {}
let currentLanguageId = getSelectedLanguageId()

// Load translations for the current language
function loadTranslations(languageId: string): Translations {
  const lang = SUPPORTED_LANGUAGES.find(l => l.id === languageId)
  if (!lang) {
    console.warn(`Unsupported language ID: ${languageId}, falling back to English`)
    return enTranslations
  }

  const loaded = translationMap[languageId]
  if (!loaded) {
    console.warn(`Translations not found for language ID: ${languageId}, falling back to English`)
    return enTranslations
  }

  return loaded
}

// Initialize translations
export function initI18n(): void {
  translations = loadTranslations(currentLanguageId)
}

// Update translations when language changes
export function updateLanguage(languageId: string): void {
  currentLanguageId = languageId
  translations = loadTranslations(languageId)
  window.dispatchEvent(new CustomEvent('i18n-updated'))
}

// Get translation by key path (e.g., "nav.dictionary" or "home.welcome")
export function t(key: TranslationKey, params?: Record<string, string>): string {
  if (key == null || typeof key !== 'string') return ''
  const keys = key.split('.')
  let value: TranslationValue = translations

  for (const k of keys) {
    if (typeof value === 'object' && value !== null && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key}`)
      return key // Return the key itself if translation not found
    }
  }

  if (typeof value !== 'string') {
    console.warn(`Translation value is not a string for key: ${key}`)
    return key
  }

  // Replace parameters if provided
  if (params) {
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
      return paramKey in params ? String(params[paramKey]) : match
    })
  }

  return value
}

// Get current language ID
export function getCurrentLanguageId(): string {
  return currentLanguageId
}

// Initialize on module load
initI18n()
